import {
  Box,
  Circle,
  Image as ImageIcon,
  Square,
  Text as TextIcon
} from 'lucide-react';
import React from 'react';
import { Shape } from '../../../../../types';
import { useMapEditor } from '../../../contexts/MapEditorContext';
import { SidebarPanel } from '../../EditorLayout/SidebarPanel';
import { SpotPanelControls } from '../SpotPanel/SpotPanelControls';
import { CommonColorPanelControls } from './common/CommonColorPanelControls';
import { CommonPositionPanelControls } from './common/CommonPositionPanelControls';
import { CommonSizePanelControls } from './common/CommonSizePanelControls';
import { CommonTextPanelControls } from './common/CommonTextPanelControls';
import { RectPanelControls } from './RectPanelControls';

type ShapePanelProps = {
  id: string;
  onDetach: (
    id: string,
    content: React.ReactNode,
    title: string,
    icon: any
  ) => void;
  isDetached: boolean;
};

export interface ShapeSectionOptions {
  shapes: Shape[];
}

interface ShapeSectionProperties {
  title: string;
  icon: React.FC;
  panels: React.FC<ShapeSectionOptions>[];
}

const getShapeSection = (shapes: Shape[]): ShapeSectionProperties | null => {
  const shape = shapes[0];
  if (!shape) return null;
  const isSameType = shapes.every((_shape) => _shape.type === shape.type);
  const type = isSameType ? shape.type : 'mixed';
  switch (type) {
    case 'mixed':
      return null;
    case 'spot':
      return {
        title: 'Resource',
        icon: Circle,
        panels: [
          CommonColorPanelControls,
          SpotPanelControls,
          CommonTextPanelControls
        ]
      };
    case 'rectangle':
      return {
        title: 'Rectangle',
        icon: Square,
        panels: [
          RectPanelControls,
          CommonColorPanelControls,
          CommonPositionPanelControls
        ]
      };
    case 'circle':
      return {
        title: 'Rectangle',
        icon: Square,
        panels: [CommonColorPanelControls, CommonPositionPanelControls]
      };
    case 'furniture':
      return {
        title: 'Furniture',
        icon: Box,
        panels: [
          CommonColorPanelControls,
          CommonSizePanelControls,
          CommonTextPanelControls
        ]
      };
    case 'icon':
      return {
        title: 'Icon',
        icon: ImageIcon,
        panels: [
          CommonColorPanelControls,
          CommonSizePanelControls,
          CommonTextPanelControls
        ]
      };
    case 'text':
      return {
        title: 'Text',
        icon: TextIcon,
        panels: [CommonTextPanelControls, CommonPositionPanelControls]
      };
  }
  return {
    title: 'Shape',
    icon: Square,
    panels: [CommonPositionPanelControls]
  };
};

export const ShapePanel: React.FC<ShapePanelProps> = ({
  id,
  onDetach,
  isDetached
}) => {
  const { shapes, selectedIds } = useMapEditor();

  const selectedSection = React.useMemo(() => {
    const selectedShapes = shapes.filter((shape) =>
      selectedIds.includes(shape.id)
    );
    if (!selectedShapes) return null;
    return getShapeSection(selectedShapes);
  }, [shapes, selectedIds]);

  const selectedShapes = shapes.filter((shape) =>
    selectedIds.includes(shape.id)
  );

  return (
    <SidebarPanel
      title={selectedSection?.title || 'Shape'}
      icon={selectedSection?.icon || Box}
      id={id}
      onDetach={onDetach}
      isDetached={isDetached}
    >
      {!selectedSection && (
        <div className="p-4 text-sm text-gray-500 text-center">
          No selection
        </div>
      )}
      <div className={'pb-6'}>
        {selectedSection?.panels.map((Panel) => (
          <Panel key={Panel.displayName} shapes={selectedShapes} />
        ))}
      </div>
      {/* TODO - To remove, development feature */}
      <div className={'text-xs text-gray-500 text-center px-6 border-t pt-6'}>
        {selectedShapes.map((shape) => shape.id).join(', ')}
      </div>
    </SidebarPanel>
  );
};
